import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  getCompanyList,
  getCompanyStatusCount,
  activeCompany,
  deactiveCompany,
} from "services/data.internal.service";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, ActiveButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function CompanyList() {
  const navigate = useNavigate();
  const {permissions, allowAll} = useTeamValue();
  const {customerCode} = useCustomerValue()
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["company-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };

  const addPermission = allowAll || (Boolean(permissions["Company"]) && permissions["Company"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Company"]) && permissions["Company"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Company"]) && permissions["Company"].findIndex(b => b==="deactivate") !== -1);

  useQuery({
    queryKey: ["company-list", { customerCode, active }],
    queryFn: () => getCompanyList({ active }),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["company-list", "status", { customerCode }],
    queryFn: () => getCompanyStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactiveCompany, { onSuccess, onError });
  const activeMutation = useMutation(activeCompany, { onSuccess, onError });

  const onNew = () => navigate("/setup/company/add");

  const onEdit = (companyCode) => navigate("/setup/company/edit", { state: { companyCode } });

  const onDelete = (companyCode) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${companyCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate({ companyCode });
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (companyCode) => {
    setDialog(
      <MessageDialog
        title={`Activate ${companyCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate({ companyCode });
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Company Code", accessorKey: "companyCode" },
    { header: "Company Name", accessorKey: "companyName" },
    { header: "Short Name", accessorKey: "shortName" },
    { header: "Phone No", accessorKey: "phoneNo" },
    { header: "Fax No", accessorKey: "faxNo" },
    { header: "Email", accessorKey: "email" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { companyCode } = row.original;
        if (active) {
          return (
            <>
              <EditButton title="edit company" disabled={!editPermission} onClick={() => onEdit(companyCode)} />
              <DelButton title="deactivate company" disabled={!deactivatePermission} onClick={() => onDelete(companyCode)} />
            </>
          );
        } else {
          return <ActiveButton title="activate company" disabled={!editPermission} onClick={() => onActive(companyCode)} />;
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton variant="gradient" color="info" disabled={!addPermission} onClick={onNew}>
            <Icon>add</Icon>&nbsp;company
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default CompanyList;
